import { Patient } from "@/types/Patient"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { API_URL, QUERY_KEYS } from "../constants"
import { LabReport, Recommendation } from "../types/LabReport"

export const useGetLabReports = (orgId: string, token: string) =>
  useQuery<{ data: LabReport[]; errors: string[] }, Error>({
    queryKey: QUERY_KEYS.labReports(orgId),
    queryFn: async () => {
      const res = await fetch(`${API_URL}/organizations/${orgId}/lab-reports/`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
        },
      })
      if (!res.ok) {
        const parsed = JSON.parse(await res.json())
        throw new Error(parsed.error ?? parsed.message ?? parsed ?? "Internal Server Error")
      }
      const json = await res.json()
      if (json.error) {
        throw new Error(json.error)
      }
      return json
    },
  })

export const useGetLabReport = (orgId: string, id: string, token: string, anonToken?: string) =>
  useQuery<{ labReport: LabReport; recommendations: Recommendation[]; patient: Patient }, Error>({
    queryKey: QUERY_KEYS.labReport(orgId, id),
    queryFn: async () => {
      // Use anonymous token if available, otherwise use regular token
      const authToken = anonToken || token

      const res = await fetch(`${API_URL}/organizations/${orgId}/lab-reports/${id}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          Authorization: `Bearer ${authToken}`,
        },
      })

      if (!res.ok) {
        const parsed = await res.json()
        throw new Error(parsed.error ?? parsed.message ?? "Internal Server Error")
      }

      const json = await res.json()
      if (json.error) {
        throw new Error(json.error)
      }

      return json
    },
    enabled: !!(token || anonToken) && !!orgId && !!id,
  })

export const useAssignPatient = (orgId: string, token: string) => {
  const queryClient = useQueryClient()

  return useMutation<{ message: string; labReport: LabReport }, Error, { labId: string; patientId: string }>({
    mutationFn: async ({ labId, patientId }) => {
      const res = await fetch(`${API_URL}/organizations/${orgId}/lab-reports/${labId}/patient`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ patientId }),
      })

      if (!res.ok) {
        const parsed = await res.json()
        throw new Error(parsed.error ?? parsed.message ?? parsed ?? "Internal Server Error")
      }

      const json = await res.json()
      if (json.error) {
        throw new Error(json.error)
      }

      return json
    },
    onSuccess: (data, variables) => {
      // Invalidate and refetch lab reports list
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.labReports(orgId) })
      // Invalidate and refetch the specific lab report
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.labReport(orgId, variables.labId) })
    },
  })
}

export const useSyncLabReports = (orgId: string, token: string) => {
  const queryClient = useQueryClient()

  return useMutation<
    { newReports: number; totalReports: number; message: string },
    Error,
    { provider: "ANTECH" | "IDEXX" | "ZOETIS"; startDate: string; endDate: string }
  >({
    mutationFn: async ({ provider, startDate, endDate }) => {
      const res = await fetch(`${API_URL}/organizations/${orgId}/lab-reports/sync`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          provider,
          startDate: new Date(startDate).toISOString(),
          endDate: new Date(endDate).toISOString(),
        }),
      })

      if (!res.ok) {
        const parsed = await res.json()
        throw new Error(parsed.error ?? parsed.message ?? parsed ?? "Internal Server Error")
      }

      const json = await res.json()
      if (json.error) {
        throw new Error(json.error)
      }

      return json
    },
    onSuccess: () => {
      // Invalidate and refetch lab reports list after sync
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.labReports(orgId) })
    },
  })
}
